import * as React from 'react';
import { formatDate } from 'vet-bones/utils';

import { ResourcePreviewHeader } from 'src/components/resourceDialog/ResourcePreviewHeader';
import {
  PreviewEmptyState,
  PreviewErrorState,
  PreviewLoadingState,
  PreviewNoSelectionState,
} from 'src/components/resourceDialog/ResourcePreviewStates';
import { ResourcePreviewTable } from 'src/components/resourceDialog/ResourcePreviewTable';
import { TEST_IDS } from 'src/constants/testIds';
import { ProjectVirtualGraph } from 'src/store/state/projects';
import * as copy from 'src/templates/copy';

type VirtualGraphPreviewProps = {
  virtualGraph: ProjectVirtualGraph;
  resourceName: string;
  onResourceNameChange: (name: string) => void;
  onFieldLabelChange?: (value: string, label: string) => void;
  onRefresh?: () => void;
  isLoading?: boolean;
  error?: string;
  /** Number of rows in the preview (for row count display) */
  rowCount?: number | null;
};

export const VirtualGraphPreview: React.VFC<VirtualGraphPreviewProps> = ({
  virtualGraph,
  resourceName,
  onResourceNameChange,
  onFieldLabelChange,
  onRefresh,
  isLoading,
  error,
  rowCount,
}) => {
  const lastModified = virtualGraph.dateLastUpdated
    ? formatDate(new Date(virtualGraph.dateLastUpdated))
    : null;

  const hasPartialData = !virtualGraph.allResultsIncluded;

  if (isLoading) {
    return <PreviewLoadingState />;
  }

  if (error) {
    return (
      <PreviewErrorState
        message={error}
        title={copy.components.virtualGraphPreviewDialog.errorTitle}
      />
    );
  }

  const hasNoTableSelected =
    typeof virtualGraph.from === 'object' && !virtualGraph.from.name;

  if (hasNoTableSelected) {
    return (
      <PreviewNoSelectionState
        subtitle={
          copy.components.virtualGraphPreviewDialog.noTableSelected.subtitle
        }
        title={copy.components.virtualGraphPreviewDialog.noTableSelected.title}
      />
    );
  }

  if (!virtualGraph.fields.length) {
    return (
      <PreviewEmptyState
        message={
          copy.components.projectResourceDialog.projectVirtualGraphPanel.preview
            .errors.previewEmpty
        }
      />
    );
  }

  const rowCountText =
    rowCount !== null && rowCount !== undefined
      ? copy.components.projectResourceDialog.projectVirtualGraphPanel.form.previewRowCount(
          rowCount,
          Boolean(virtualGraph.allResultsIncluded)
        )
      : null;

  return (
    <>
      <ResourcePreviewHeader
        lastModified={lastModified}
        nameInputId="virtual-graph-name-input"
        nameInputTestId={TEST_IDS.projectVirtualGraphPanel.resourceName}
        namePlaceholder={
          copy.components.virtualGraphPreviewDialog.resourceNamePlaceholder
        }
        onRefresh={onRefresh}
        onResourceNameChange={onResourceNameChange}
        resourceName={resourceName}
        rowCountTestId={TEST_IDS.projectVirtualGraphPanel.preview.rowCount}
        rowCountText={rowCountText}
        showPartialDataWarning={hasPartialData}
      />
      <ResourcePreviewTable
        fields={virtualGraph.fields}
        onFieldLabelChange={onFieldLabelChange}
        rows={virtualGraph.previewRows}
        testId={TEST_IDS.projectVirtualGraphPanel.preview.table}
      />
    </>
  );
};
